import { useState, useEffect } from "react";

import {
  changeRAndDUpperCase,
  IconExternalLink,
  IconInfo,
} from "@frontend/common";

import { useArticleInfoQuery } from "@api/articleInfo/useArticleInfo";
import { useDealsOverlappedUpdateMutation } from "@api/dealsOverlappedUpdate/dealsOverlappedUpdate";
import {
  FundingSimilarPressData,
  FundingTargetPressData,
  SimilarPressData,
  TargetPressData,
} from "@api/dealsSimilarData/dealsSimilarData.type";
import { useOverlappedPressCancelMutation } from "@api/unsuitablePress/unsuitablePress";
import { formatDate } from "@utils/formatDate";
import { trimCharacter } from "@utils/trimCharacter";

import { SIMILARITY_PRESS_INFO_TEXT } from "./constants/similarityPress.constant";
import * as S from "./SimilarPressTableContainer.style";
import { getSimilarityClassificationDisplayName } from "./utils/getSimilarityClassificationDisplayName";
import { isWithinFourDays } from "./utils/isWithinFourDays";
import { moveToFillInTable } from "./utils/moveToFillInTable";
import DealSimilarityPressTableRow from "../DealSimilarityPressTableRow/DealSimilarityPressTableRow";
import { OverFlowTooltip } from "../OverflowTooltip/OverflowTooltip";
import { Checkbox } from "../OverlappedCheckBox/OverlappedCheckBox";

const SimilarPressTableContainer = ({
  similarPressData,
  isResizing,
  targetPressData,
  similarDataType,
}: {
  similarPressData: SimilarPressData[] | FundingSimilarPressData[];
  isResizing: boolean;
  targetPressData: TargetPressData | FundingTargetPressData;
  similarDataType: "deal" | "funding";
}) => {
  /**
   * Unsuitable 체크박스 여부
   * 현재 구현
   * target press(현재 켜져있는 press)가 draftArticleId로 설정
   * dupleArticleId는 similar press id로 설정됨
   *
   * 예외 사항 추가 필요
   * target press 혹은 similar press가 deal - deal filing이 혼합되어 있는 경우
   * filing이 draftArticleId로 설정
   * press가 duplArticleId로 설정
   *
   * 결국 봐야하는 부분은 Checkbox 체크 여부와 체크시 API 호출
   */

  const DATA_TYPE_MAP = {
    C006: "PRESS",
    C021: "PRESS",
    C801: "FILING",
    C802: "PRESS", // Funding Filing은 Press Filing 구분없이 Overlapped 처리가 필요
  } as const;
  const targetCategoryType =
    DATA_TYPE_MAP?.[
      targetPressData.classification as keyof typeof DATA_TYPE_MAP
    ] || "PRESS";

  const [overlappedId, setOverlappedId] = useState<number>();
  const [checked, setChecked] = useState<boolean>(false);
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);

  const { mutateAsync: updateOverlapped } = useDealsOverlappedUpdateMutation();
  const { mutateAsync: cancelOverlapped } = useOverlappedPressCancelMutation();

  const changeCheckedCheckbox = (isChecked: boolean) => {
    setChecked(isChecked);
  };

  const { data: articleInfo } = useArticleInfoQuery();
  const mcEditorName = trimCharacter(articleInfo?.mcEditorName);

  useEffect(() => {
    setOverlappedId(articleInfo?.duplArticleId);
    setChecked(!!articleInfo?.duplArticleId);
  }, [articleInfo]);

  const handleCheckboxChange = async ({
    currentSimilarPressId,
    currentSimilarCategoryType,
    currentTargetCategoryType,
    similarityPressDuplId,
    similarityPressRejectReason,
  }: {
    currentSimilarPressId: number;
    currentSimilarCategoryType: "PRESS" | "FILING";
    currentTargetCategoryType: "PRESS" | "FILING";
    similarityPressDuplId: number;
    similarityPressRejectReason: string;
  }) => {
    const newChecked = !checked;
    setChecked(newChecked);

    try {
      setOverlappedId(newChecked ? currentSimilarPressId : undefined);
      const isDifferentCategoryType =
        currentSimilarCategoryType !== currentTargetCategoryType;
      const isCancelCondition = isDifferentCategoryType
        ? currentSimilarCategoryType === "PRESS"
          ? currentSimilarPressId === articleInfo?.duplArticleId &&
            articleInfo?.articleRejectReason?.startsWith("Overlapped press")
          : similarityPressDuplId === articleInfo?.draftArticleId &&
            similarityPressRejectReason?.startsWith("Overlapped press")
        : currentSimilarPressId === articleInfo?.duplArticleId &&
          articleInfo?.articleRejectReason?.startsWith("Overlapped press");

      if (isCancelCondition) {
        await cancelOverlapped({
          currentTargetPressId: targetPressData.pressId,
          currentSimilarPressId,
          currentSimilarCategoryType,
          currentTargetCategoryType,
        });

        return;
      }

      const selectedSimilarPress = similarPressData.find(
        (item) => item.pressId === currentSimilarPressId,
      );

      const targetClassification = targetPressData.classification;
      const selectedClassification = selectedSimilarPress?.classification;

      const articleRejectReason =
        (targetClassification === "C006" &&
          selectedClassification === "C006") ||
        (targetClassification === "C021" && selectedClassification === "C021")
          ? "Overlapped press without merging"
          : targetClassification === "C801" ||
            selectedClassification === "C801" ||
            targetClassification === "C802" ||
            selectedClassification === "C802"
          ? "Overlapped press with merging"
          : "Overlapped press";
      const targetId = targetPressData.pressId;
      const similarId = currentSimilarPressId;
      const isMcEditor = !!mcEditorName;
      await updateOverlapped({
        targetId,
        similarId,
        isMcEditor,
        articleRejectReason,
        currentSimilarCategoryType,
        currentTargetCategoryType,
      });
    } catch (error) {
      setChecked(!checked);
      setOverlappedId(articleInfo?.duplArticleId);
    }
  };

  const hasTargetC801 = targetPressData.classification === "C801";
  const hasSimilarC801 = similarPressData.some(
    (item) => item.classification === "C801",
  );
  const hasTargetC802 = targetPressData.classification === "C802";
  const hasSimilarC802 = similarPressData.some(
    (item) => item.classification === "C802",
  );

  const shouldShowFilingInfo =
    ((hasTargetC801 || hasSimilarC801) && !(hasTargetC801 && hasSimilarC801)) ||
    ((hasTargetC802 || hasSimilarC802) && !(hasTargetC802 && hasSimilarC802));

  if (similarDataType === "funding") {
    const fundingSimilarData = similarPressData as FundingSimilarPressData[];
    const fundingTargetData = targetPressData as FundingTargetPressData;

    return (
      <S.SimilarPressTableContainer isResizing={isResizing}>
        <S.SimilarPressTableTitleWrapper>
          <S.SimilarPressTableTitle>Similar Press</S.SimilarPressTableTitle>
          {shouldShowFilingInfo && (
            <S.SimilarPressTableTitleDescriptionWrapper>
              <IconInfo width={14} height={14} color="#7C899C" />
              <S.SimilarPressTableTitleDescription>
                {SIMILARITY_PRESS_INFO_TEXT}
              </S.SimilarPressTableTitleDescription>
            </S.SimilarPressTableTitleDescriptionWrapper>
          )}
        </S.SimilarPressTableTitleWrapper>
        <S.SimilarPressTableWrapper>
          <S.SimilarPressTableHeader>
            <S.SimilarPressTablePressId>
              <S.SimilarPressTableHeadDiv>Press ID</S.SimilarPressTableHeadDiv>
            </S.SimilarPressTablePressId>
            <S.SimilarPressTableScore>
              <S.SimilarPressTableHeadDiv>Score</S.SimilarPressTableHeadDiv>
            </S.SimilarPressTableScore>
            <S.SimilarPressTablePressDate>
              <S.SimilarPressTableHeadDiv>
                Press Date
              </S.SimilarPressTableHeadDiv>
            </S.SimilarPressTablePressDate>
            <S.SimilarPressTableInvestee>
              <S.SimilarPressTableHeadDiv>Investee</S.SimilarPressTableHeadDiv>
            </S.SimilarPressTableInvestee>
            <S.SimilarPressTableInvestor>
              <S.SimilarPressTableHeadDiv>Investor</S.SimilarPressTableHeadDiv>
            </S.SimilarPressTableInvestor>
            <S.SimilarPressTableClassification>
              <S.SimilarPressTableHeadDiv>
                Classification
              </S.SimilarPressTableHeadDiv>
            </S.SimilarPressTableClassification>
            <S.SimilarPressTableRoundType>
              <S.SimilarPressTableHeadDiv>
                Round Type
              </S.SimilarPressTableHeadDiv>
            </S.SimilarPressTableRoundType>
            <S.SimilarPressTableAmount>
              <S.SimilarPressTableHeadDiv>Amount</S.SimilarPressTableHeadDiv>
            </S.SimilarPressTableAmount>
            <S.SimilarPressTablePressTitle>
              <S.SimilarPressTableHeadDiv>
                Press Title
              </S.SimilarPressTableHeadDiv>
            </S.SimilarPressTablePressTitle>
            <S.SimilarPressTableUnsuitable>
              <S.SimilarPressTableHeadDiv>
                Unsuitable
              </S.SimilarPressTableHeadDiv>
            </S.SimilarPressTableUnsuitable>
          </S.SimilarPressTableHeader>

          {fundingSimilarData?.map((similarPressItem) => {
            return (
              <S.SimilarPressTableBody
                key={similarPressItem.pressId}
                onMouseEnter={() => setHoveredRow(similarPressItem.pressId)}
                onMouseLeave={() => setHoveredRow(null)}
                hovered={hoveredRow === similarPressItem.pressId}
              >
                <S.SimilarPressTablePressIdWrapper>
                  <S.SimilarPressId
                    onClick={() =>
                      moveToFillInTable(`${similarPressItem.pressId}`)
                    }
                  >
                    #{similarPressItem.pressId}
                  </S.SimilarPressId>
                  <S.SimilarPressIcon
                    onClick={() =>
                      moveToFillInTable(`${similarPressItem.pressId}`)
                    }
                  >
                    <IconExternalLink width={20} height={20} />
                  </S.SimilarPressIcon>
                </S.SimilarPressTablePressIdWrapper>

                <S.SimilarPressTableScore>
                  <span>{similarPressItem.score.toFixed(4)}</span>
                </S.SimilarPressTableScore>

                <S.SimilarPressTablePressDateData>
                  <div>
                    <S.SimilarPressCheckDate
                      isWithinFourDays={isWithinFourDays(
                        similarPressItem.pressDate[0]?.text,
                        fundingTargetData.pressDate[0]?.text,
                      )}
                    >
                      {formatDate(
                        similarPressItem.pressDate[0]?.text,
                        "YYYY.MM.DD",
                      )}
                    </S.SimilarPressCheckDate>
                    {similarPressItem.pressDate.map((pressDate) => (
                      <S.SimilarPressCheckDate
                        isWithinFourDays={isWithinFourDays(
                          similarPressItem.pressDate[0]?.text,
                          fundingTargetData.pressDate[0]?.text,
                        )}
                      >
                        {pressDate.isManual ? "(Manual)" : ""}
                      </S.SimilarPressCheckDate>
                    ))}
                  </div>
                </S.SimilarPressTablePressDateData>

                <S.SimilarPressTableInvestee>
                  <OverFlowTooltip
                    items={similarPressItem.investee.map((investee) => (
                      <S.PressMatchedWrapper
                        key={investee.text}
                        matched={investee.matched}
                      >
                        {investee.text}
                      </S.PressMatchedWrapper>
                    ))}
                  />
                </S.SimilarPressTableInvestee>

                <S.SimilarPressTableInvestor>
                  <OverFlowTooltip
                    items={similarPressItem.investor.map((investor) => (
                      <S.PressMatchedWrapper
                        key={investor.text}
                        matched={investor.matched}
                      >
                        {investor.text}
                      </S.PressMatchedWrapper>
                    ))}
                  />
                </S.SimilarPressTableInvestor>

                <S.SimilarPressTableClassificationData>
                  <S.SimilarPressTableBodyDiv>
                    {getSimilarityClassificationDisplayName(
                      similarPressItem.classification,
                    )}
                  </S.SimilarPressTableBodyDiv>
                </S.SimilarPressTableClassificationData>

                <S.SimilarPressTableRoundType>
                  <OverFlowTooltip
                    items={similarPressItem.roundType.map((roundType) => (
                      <S.PressMatchedWrapper
                        key={roundType.text}
                        matched={roundType.matched}
                      >
                        {changeRAndDUpperCase(roundType.text)}
                      </S.PressMatchedWrapper>
                    ))}
                  />
                </S.SimilarPressTableRoundType>
                <S.SimilarPressTableAmount>
                  <S.SimilarPressTableBodyDiv>
                    <S.PressMatchedWrapper
                      matched={similarPressItem.amount.matched}
                    >
                      {similarPressItem.amount.text}
                    </S.PressMatchedWrapper>
                  </S.SimilarPressTableBodyDiv>
                </S.SimilarPressTableAmount>

                <S.SimilarPressTablePressTitleData>
                  <OverFlowTooltip
                    items={[similarPressItem.pressTitle]}
                    maxLines={4}
                  />
                </S.SimilarPressTablePressTitleData>
                <S.SimilarPressTableUnsuitableData>
                  <div>
                    <div>Overlapped</div>
                    <S.CheckBoxContainer>
                      <Checkbox
                        onChangeHandler={() =>
                          handleCheckboxChange({
                            currentSimilarPressId: similarPressItem.pressId,
                            currentSimilarCategoryType: "PRESS",
                            currentTargetCategoryType: "PRESS",
                            similarityPressDuplId: articleInfo?.duplArticleId,
                            similarityPressRejectReason:
                              articleInfo?.articleRejectReason || "",
                          })
                        }
                        value={similarPressItem.pressId}
                        checked={similarPressItem.pressId === overlappedId}
                      />
                    </S.CheckBoxContainer>
                  </div>
                </S.SimilarPressTableUnsuitableData>
              </S.SimilarPressTableBody>
            );
          })}
        </S.SimilarPressTableWrapper>
      </S.SimilarPressTableContainer>
    );
  }
  if (similarDataType === "deal") {
    const dealSimilarData = similarPressData as SimilarPressData[];
    const dealTargetData = targetPressData as TargetPressData;

    return (
      <S.SimilarPressTableContainer isResizing={isResizing}>
        <S.SimilarPressTableTitleWrapper>
          <S.SimilarPressTableTitle>Similar Press</S.SimilarPressTableTitle>
          {shouldShowFilingInfo && (
            <S.SimilarPressTableTitleDescriptionWrapper>
              <IconInfo width={14} height={14} color="#7C899C" />
              <S.SimilarPressTableTitleDescription>
                {SIMILARITY_PRESS_INFO_TEXT}
              </S.SimilarPressTableTitleDescription>
            </S.SimilarPressTableTitleDescriptionWrapper>
          )}
        </S.SimilarPressTableTitleWrapper>
        <S.SimilarPressTableWrapper>
          <S.SimilarPressTableHeader>
            <S.SimilarPressTablePressId>
              <S.SimilarPressTableHeadDiv>Press ID</S.SimilarPressTableHeadDiv>
            </S.SimilarPressTablePressId>
            <S.SimilarPressTableScore>
              <S.SimilarPressTableHeadDiv>Score</S.SimilarPressTableHeadDiv>
            </S.SimilarPressTableScore>
            <S.SimilarPressTablePressDate>
              <S.SimilarPressTableHeadDiv>
                Press Date
              </S.SimilarPressTableHeadDiv>
            </S.SimilarPressTablePressDate>
            <S.SimilarPressTableSeller>
              <S.SimilarPressTableHeadDiv>Seller</S.SimilarPressTableHeadDiv>
            </S.SimilarPressTableSeller>
            <S.SimilarPressTableBuyer>
              <S.SimilarPressTableHeadDiv>Buyer</S.SimilarPressTableHeadDiv>
            </S.SimilarPressTableBuyer>
            <S.SimilarPressTableClassification>
              <S.SimilarPressTableHeadDiv>
                Classification
              </S.SimilarPressTableHeadDiv>
            </S.SimilarPressTableClassification>
            <S.SimilarPressTableDealType>
              <S.SimilarPressTableHeadDiv>Deal Type</S.SimilarPressTableHeadDiv>
            </S.SimilarPressTableDealType>
            <S.SimilarPressTablePressTitle>
              <S.SimilarPressTableHeadDiv>
                Press Title
              </S.SimilarPressTableHeadDiv>
            </S.SimilarPressTablePressTitle>
            <S.SimilarPressTableUnsuitable>
              <S.SimilarPressTableHeadDiv>
                Unsuitable
              </S.SimilarPressTableHeadDiv>
            </S.SimilarPressTableUnsuitable>
          </S.SimilarPressTableHeader>

          {dealSimilarData?.map((similarPressItem) => {
            const similarCategoryType =
              DATA_TYPE_MAP?.[
                similarPressItem.classification as keyof typeof DATA_TYPE_MAP
              ] || "PRESS";
            return (
              <DealSimilarityPressTableRow
                similarPressItem={similarPressItem}
                dealTargetPressDate={dealTargetData.pressDate[0]?.text}
                targetCategoryType={targetCategoryType}
                similarCategoryType={similarCategoryType}
                hoveredRow={hoveredRow}
                setHoveredRow={setHoveredRow}
                handleCheckboxChange={handleCheckboxChange}
                overlappedId={overlappedId}
                targetPressId={targetPressData.pressId}
                changeCheckedCheckbox={changeCheckedCheckbox}
                targetPressDuplId={articleInfo.duplArticleId}
              />
            );
          })}
        </S.SimilarPressTableWrapper>
      </S.SimilarPressTableContainer>
    );
  }

  return null;
};

export default SimilarPressTableContainer;
